import { X } from 'lucide-react'
import { ComponentProps, Fragment, useCallback } from 'react'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { usePanelContext } from '~/components/tables/FilterPanelProvider'
import { Button } from './ui/button'

export type Option = {
  value: string
  label: string
}

export type OptionGroup = {
  label: string
  options: Option[]
}

type SelectorProps = {
  onChange: (value: string | null) => void
  options: (string | Option | OptionGroup)[]
  placeholder?: string
  value?: string
  triggerProps?: ComponentProps<typeof SelectTrigger>
  showGroupLabel?: boolean
  allowClear?: boolean
} & React.ComponentProps<typeof Select>

export function Selector({
  onChange,
  options,
  placeholder,
  value,
  showGroupLabel = false,
  allowClear = false,
  ...props
}: SelectorProps) {
  const panelContext = usePanelContext()

  const handleOpenChange = useCallback(
    (open: boolean) => {
      panelContext?.setIsSelectOpen(open)
      props.onOpenChange?.(open)
    },
    [props.onOpenChange, panelContext],
  )

  const normalizeOption = (option: string | Option): Option => {
    if (typeof option === 'string') {
      return {
        value: option,
        label: option,
      }
    }
    return option
  }

  const isOptionGroup = (item: string | Option | OptionGroup): item is OptionGroup => {
    return typeof item === 'object' && 'options' in item && Array.isArray(item.options)
  }

  const renderOption = (option: Option) => (
    <SelectItem key={option.value} value={option.value}>
      {option.label}
    </SelectItem>
  )

  const selectValue = value || ''

  return (
    <div className="relative">
      <div className="relative w-full">
        <Select
          onOpenChange={handleOpenChange}
          onValueChange={(newValue) => onChange(newValue || null)}
          value={selectValue}
          {...props}
        >
          <SelectTrigger className="w-full" {...props.triggerProps}>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent className="z-[1301]">
            {options.map((item, index) => {
              if (isOptionGroup(item)) {
                return (
                  <Fragment key={`${item.label}-${index}`}>
                    {index > 0 && <SelectSeparator />}
                    <SelectGroup>
                      {showGroupLabel && <SelectLabel>{item.label}</SelectLabel>}
                      {item.options.map(renderOption)}
                    </SelectGroup>
                  </Fragment>
                )
              }
              return renderOption(normalizeOption(item))
            })}
          </SelectContent>
        </Select>
        {allowClear && !props.disabled && value && (
          <Button
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onChange(null)
            }}
            className="absolute end-8 top-1/2 h-4 w-4 -translate-y-1/2 p-0 hover:bg-transparent"
            aria-label="Clear selection"
            variant="ghost"
            size="icon"
          >
            <X className="h-3 w-3 opacity-50 hover:opacity-100" />
          </Button>
        )}
      </div>
    </div>
  )
}
